/**
 * Performance utilities to prevent forced reflow and optimize rendering
 */

/**
 * Batch DOM reads to avoid forced reflow
 */
export const batchDOMReads = (callbacks: (() => void)[]) => {
  if ('requestAnimationFrame' in window) {
    requestAnimationFrame(() => {
      callbacks.forEach(cb => cb());
    });
  } else {
    callbacks.forEach(cb => cb());
  }
};

/**
 * Batch DOM writes to avoid forced reflow
 */
export const batchDOMWrites = (callbacks: (() => void)[]) => {
  if ('requestAnimationFrame' in window) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        callbacks.forEach(cb => cb());
      });
    });
  } else {
    setTimeout(() => {
      callbacks.forEach(cb => cb());
    }, 0);
  }
};

/**
 * Debounce function to limit expensive operations
 */
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, wait);
  };
};

/**
 * Throttle function for scroll and resize handlers
 */
export const throttle = <T extends (...args: any[]) => void>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

/**
 * Use Intersection Observer for lazy loading
 */
export const createLazyLoader = (
  elements: HTMLElement[],
  callback: (element: HTMLElement) => void,
  options?: IntersectionObserverInit
) => {
  if (!('IntersectionObserver' in window)) {
    // Fallback for browsers that don't support Intersection Observer
    elements.forEach(callback);
    return null;
  }
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        callback(entry.target as HTMLElement);
        observer.unobserve(entry.target);
      }
    });
  }, {
    rootMargin: '50px',
    threshold: 0.01,
    ...options
  });
  
  elements.forEach(el => observer.observe(el));
  
  return observer;
};

/**
 * Optimize scroll performance by using passive listeners
 */
export const addPassiveScrollListener = (
  callback: EventListener,
  element: HTMLElement | Window = window
) => {
  const options: AddEventListenerOptions = {
    passive: true,
    capture: false
  };
  
  element.addEventListener('scroll', throttle(callback as any, 100), options);
  
  return () => {
    element.removeEventListener('scroll', callback, options);
  };
};

/**
 * Cache expensive DOM queries
 */
class DOMCache {
  private cache = new Map<string, HTMLElement | null>();
  
  get(selector: string): HTMLElement | null {
    if (!this.cache.has(selector)) {
      this.cache.set(selector, document.querySelector(selector));
    }
    return this.cache.get(selector) || null;
  }
  
  clear() {
    this.cache.clear();
  }
  
  invalidate(selector: string) {
    this.cache.delete(selector);
  }
}

export const domCache = new DOMCache();

/**
 * Defer non-critical JavaScript
 */
export const deferScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      });
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      setTimeout(() => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      }, 1);
    }
  });
};

/**
 * Optimize animation performance
 */
export const optimizeAnimation = (callback: FrameRequestCallback) => {
  let ticking = false;
  
  return () => {
    if (!ticking) {
      requestAnimationFrame(callback);
      ticking = true;
      
      setTimeout(() => {
        ticking = false;
      }, 16); // ~60fps
    }
  };
};

export default {
  batchDOMReads,
  batchDOMWrites,
  debounce,
  throttle,
  createLazyLoader,
  addPassiveScrollListener,
  domCache,
  deferScript,
  optimizeAnimation
};